import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bluetooth } from "lucide-react";
import { useLoan } from "../hooks/useLoan";
import { LOAN_STATUS, slotStatus, slotImage, formatCountdown } from "../lib/loans";

const theme = {
  light: {
    bg: "#f8fafc", bg2: "#f1f5f9", bg3: "#e9eef5", border: "#e2e8f0",
    text1: "#0f172a", text2: "#64748b", text3: "#94a3b8",
    blue: "#2563eb", blueBg: "#eff6ff", blueBorder: "#bfdbfe",
    green: "#16a34a", greenBg: "#f0fdf4", greenBorder: "#bbf7d0",
    red: "#dc2626", redBg: "#fef2f2", redBorder: "#fecaca",
    orange: "#ea580c", orangeBg: "#fff7ed", orangeBorder: "#fed7aa",
    cardBg: "#ffffff",
  },
  dark: {
    bg: "#0f172a", bg2: "#131f30", bg3: "#1a2740", border: "#273549",
    text1: "#f1f5f9", text2: "#94a3b8", text3: "#475569",
    blue: "#378ADD", blueBg: "#1e3a5f", blueBorder: "#1d4ed8",
    green: "#4ade80", greenBg: "#052e16", greenBorder: "#166534",
    red: "#f87171", redBg: "#2d1515", redBorder: "#7f1d1d",
    orange: "#fb923c", orangeBg: "#2d1a00", orangeBorder: "#92400e",
    cardBg: "#1e293b",
  },
};

const SLOT_COLLECTION = "headphone_slots";

const STATUS_BADGE = {
  available:      { label: "DISPO",       bg: "#16a34a" },
  borrowed:       { label: "PRIS",        bg: "#dc2626" },
  return_pending: { label: "EN RETOUR",   bg: "#f59e0b" },
  defective:      { label: "HORS SERVICE", bg: "#64748b" },
  maintenance:    { label: "HORS SERVICE", bg: "#64748b" },
};

function HeadphonesVisual({ slots, mineDocId, t }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
      {slots.map(slot => {
        const status = slotStatus(slot);
        const isMine = slot.docId === mineDocId;
        const badge = isMine ? { label: "LE VÔTRE", bg: t.blue } : STATUS_BADGE[status];
        const img = slotImage(slot);
        return (
          <div key={slot.docId} style={{
            borderRadius: 16, padding: "14px 10px 12px",
            display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
            position: "relative", overflow: "hidden",
            opacity: status === "available" || isMine ? 1 : 0.45,
          }}>
            {img
              ? <img src={img} alt={slot.brand || ""} style={{ width: "80%", aspectRatio: "1/1", objectFit: "contain" }} onError={e => { e.target.style.display = "none"; }} />
              : <div style={{ fontSize: 52, lineHeight: 1 }}>🎧</div>}
            <div style={{
              position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)",
              background: badge.bg, borderRadius: 20, padding: "2px 8px",
              fontSize: 9, fontWeight: 700, color: "#fff", whiteSpace: "nowrap",
            }}>
              {badge.label}
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: t.text1 }}>{slot.brand || slot.name}</div>
              {slot.model && <div style={{ fontSize: 10, color: t.text2 }}>{slot.model}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Step({ n, accent, children }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 12 }}>
      <div style={{
        width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
        background: accent + "22", border: `1.5px solid ${accent}55`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 10, fontWeight: 800, color: accent, marginTop: 2,
      }}>{n}</div>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

export default function HeadphonesPage() {
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => localStorage.getItem("ws-theme") === "dark");
  const t = dark ? theme.dark : theme.light;

  const {
    user, ready, slots, availableSlots, loan, loanSlot,
    secondsLeft, overdue, busy, error, clearError, justClosed,
    borrow, declareReturn,
  } = useLoan(SLOT_COLLECTION);

  const toggleTheme = () => setDark(d => {
    localStorage.setItem("ws-theme", !d ? "dark" : "light");
    return !d;
  });

  const availableCount = availableSlots.length;
  const accent = loanSlot?.accentColor || t.blue;
  const brand = loanSlot?.brand || loan?.slotName;

  let view;
  if (!ready) view = "loading";
  else if (!user) view = "login";
  else if (justClosed && !loan) view = "done";
  else if (!loan) view = error === "none" ? "none" : "idle";
  else if (loan.status === LOAN_STATUS.RETURN_PENDING) view = "pending";
  else view = "guide";

  return (
    <div style={{ background: t.bg, minHeight: "100vh", fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        @keyframes fadeIn { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pop { 0%{transform:scale(0.85);opacity:0} 70%{transform:scale(1.05)} 100%{transform:scale(1);opacity:1} }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
      `}</style>

      <div style={{ maxWidth: 400, margin: "0 auto", padding: "0 18px", minHeight: "100vh", display: "flex", flexDirection: "column" }}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 16 }}>
          <img src="/logo.PNG" alt="WeStudy" style={{ height: 28, objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={toggleTheme} style={{ width: 36, height: 20, borderRadius: 20, cursor: "pointer", position: "relative", background: t.bg3, border: `1px solid ${t.border}` }}>
              <div style={{ width: 14, height: 14, borderRadius: "50%", background: t.blue, position: "absolute", top: 2, left: 2, transition: "transform 0.2s", transform: dark ? "translateX(16px)" : "translateX(0)" }} />
            </button>
            <button onClick={() => navigate("/equipment")} style={{ fontSize: 12, borderRadius: 8, padding: "6px 10px", cursor: "pointer", background: t.bg3, border: `1px solid ${t.border}`, color: t.text2 }}>← Retour</button>
          </div>
        </div>

        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", paddingBottom: 60 }}>

          {view === "loading" && <div style={{ textAlign: "center", color: t.text3, fontSize: 13 }}>Chargement…</div>}
          {view === "login" && <div style={{ textAlign: "center", color: t.text2, fontSize: 14 }}>Connectez-vous pour emprunter un casque.</div>}

          {/* ── IDLE ── */}
          {view === "idle" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <HeadphonesVisual slots={slots} mineDocId={null} t={t} />
              <div style={{ fontSize: 22, fontWeight: 800, color: t.text1, marginBottom: 8 }}>Casques Audio</div>
              <div style={{ fontSize: 14, color: t.text2, marginBottom: 20, lineHeight: 1.6 }}>
                {availableCount > 0
                  ? `${availableCount} casque${availableCount > 1 ? "s" : ""} disponible${availableCount > 1 ? "s" : ""}`
                  : "Aucun casque disponible"}
              </div>
              {error && error !== "none" && <div style={{ fontSize: 12, color: t.red, marginBottom: 12 }}>{error}</div>}
              <button
                onClick={borrow}
                disabled={busy || availableCount === 0}
                style={{
                  width: "100%", border: "none", borderRadius: 14,
                  background: availableCount > 0 ? t.blue : t.bg3,
                  color: availableCount > 0 ? "#fff" : t.text3,
                  fontSize: 16, fontWeight: 700, padding: 16,
                  cursor: availableCount > 0 ? "pointer" : "not-allowed",
                  opacity: availableCount > 0 && !busy ? 1 : 0.6,
                }}
              >
                🎧 Emprunter un casque
              </button>
              <div style={{ fontSize: 11, color: t.text3, marginTop: 14 }}>Retour validé par l'accueil WeStudy</div>
            </div>
          )}

          {/* ── GUIDE (emprunt actif) ── */}
          {view === "guide" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: t.text1, marginBottom: 2 }}>{brand}</div>
              {loanSlot?.model && <div style={{ fontSize: 12, color: t.text2, marginBottom: 12 }}>{loanSlot.model}</div>}

              {secondsLeft !== null && (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: overdue ? t.redBg : t.orangeBg, border: `1px solid ${overdue ? t.redBorder : t.orangeBorder}`, borderRadius: 20, padding: "4px 12px", marginBottom: 16, animation: overdue ? "pulse 1s infinite" : "none" }}>
                  <span style={{ fontSize: 12, color: overdue ? t.red : t.orange, fontWeight: 600 }}>
                    {overdue ? "⚠️ Temps écoulé — merci de le rendre" : `⏱ ${formatCountdown(secondsLeft)} restantes`}
                  </span>
                </div>
              )}

              <div style={{ background: t.cardBg, border: `1.5px solid ${t.border}`, borderRadius: 16, padding: 16, marginBottom: 14, textAlign: "left" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                  <Bluetooth size={24} color={accent} strokeWidth={2.2} />
                  <div style={{ fontSize: 13, fontWeight: 700, color: t.text1 }}>Connexion Bluetooth</div>
                </div>

                <Step n={1} accent={accent}>
                  <div style={{ fontSize: 12, color: t.text1, lineHeight: 1.6, marginBottom: 10 }}>
                    Maintenez le bouton Power <strong>3 secondes</strong> jusqu'à ce que les voyants <strong>rouge et bleu</strong> clignotent alternativement.
                  </div>
                  <div style={{ borderRadius: 12, overflow: "hidden", border: `1px solid ${t.border}`, background: t.bg2 }}>
                    <img src="/step.png" alt="Appairage" style={{ width: "100%", display: "block" }} onError={e => { e.target.parentElement.style.display = "none"; }} />
                  </div>
                </Step>
                <Step n={2} accent={accent}>
                  <div style={{ fontSize: 12, color: t.text1, lineHeight: 1.5, paddingTop: 3 }}>
                    Activez le <strong>Bluetooth</strong> sur votre téléphone et recherchez l'appareil dans la liste.
                  </div>
                </Step>
                <Step n={3} accent={accent}>
                  <div style={{ fontSize: 12, color: t.text1, lineHeight: 1.5, paddingTop: 3 }}>
                    Sélectionnez le casque — une <strong>voix de confirmation</strong> retentira une fois connecté. 🎵
                  </div>
                </Step>
              </div>

              <div style={{ background: t.blueBg, border: `1px solid ${t.blueBorder}`, borderRadius: 12, padding: "10px 14px", marginBottom: 20, display: "flex", gap: 8, alignItems: "center", textAlign: "left" }}>
                <span style={{ fontSize: 15, flexShrink: 0 }}>ℹ️</span>
                <span style={{ fontSize: 11, color: t.blue, lineHeight: 1.5 }}>
                  Remettez le casque à <strong>l'accueil WeStudy</strong> une fois terminé, puis appuyez ci-dessous.
                </span>
              </div>

              {error && <div style={{ fontSize: 12, color: t.red, marginBottom: 12 }}>{error}</div>}
              <button disabled={busy} onClick={() => { clearError(); declareReturn(); }} style={{ width: "100%", border: "none", borderRadius: 14, background: t.red, color: "#fff", fontSize: 15, fontWeight: 700, padding: 15, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1 }}>
                🎧 J'ai rendu le casque
              </button>
            </div>
          )}

          {/* ── PENDING ── */}
          {view === "pending" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ position: "relative", width: 100, height: 100, margin: "0 auto 28px" }}>
                <svg width="100" height="100" style={{ animation: "spin 1.6s linear infinite" }}>
                  <circle cx="50" cy="50" r="42" fill="none" stroke={t.bg3} strokeWidth="6" />
                  <circle cx="50" cy="50" r="42" fill="none" stroke={t.orange} strokeWidth="6" strokeLinecap="round" strokeDasharray="70 194" />
                </svg>
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32 }}>🎧</div>
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, color: t.text1, marginBottom: 8 }}>Retour en attente de validation</div>
              <div style={{ fontSize: 13, color: t.text2, lineHeight: 1.6 }}>L'accueil va vérifier le casque <strong style={{ color: t.text1 }}>{brand}</strong>.<br />Vous pouvez quitter cette page.</div>
            </div>
          )}

          {/* ── DONE ── */}
          {view === "done" && (
            <div style={{ animation: "pop 0.4s ease", textAlign: "center" }}>
              <div style={{ width: 80, height: 80, borderRadius: "50%", background: t.greenBg, border: `2px solid ${t.green}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, margin: "0 auto 20px" }}>✅</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: t.text1, marginBottom: 8 }}>Merci !</div>
              <div style={{ fontSize: 13, color: t.text2 }}>{justClosed === "returned" ? "Retour validé par l'accueil." : "Votre emprunt a été clôturé."}</div>
            </div>
          )}

          {/* ── NONE ── */}
          {view === "none" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>😔</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: t.text1, marginBottom: 8 }}>Aucun casque disponible</div>
              <div style={{ fontSize: 13, color: t.text2, marginBottom: 32 }}>Quelqu'un vient de prendre le dernier.</div>
              <button onClick={clearError} style={{ border: "none", borderRadius: 12, background: t.bg3, color: t.text2, fontSize: 14, fontWeight: 600, padding: "12px 24px", cursor: "pointer" }}>← Réessayer</button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}